import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation } from "@dnd-kit/react/sortable";
import React, { useCallback, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { Button } from "../../Button";
import { FormStateStatus } from "../../utils/FormStateStatus";
import { classNames } from "../../utils/classNames";
import { FormHeader } from "./FormBuilderHeader";
import { DraggableFormRow, NewFormRow, StaticFormRow } from "./FormBuilderRow";
const styles = {
    table: {
        paddingBottom: "12px",
    },
    body: {
        "& form:nth-child(odd)": {
            background: "#F3F9FC",
        },
    },
    row: {
        display: "flex",
    },
    cell: {
        flex: "4 4 0",
        padding: "4.5px 5px 4px 5px",
        borderBottom: "1px solid #D5DFE7",
        "& .select__single-value": {
            top: "45%",
        },
        "& .form-group": {
            marginBottom: 0,
        },
        "& .form-control-static": {
            minHeight: "initial",
            paddingBottom: 0,
        },
        minHeight: 30,
    },
    rowDragging: {
        borderTop: "1px solid #D5DFE7",
        backgroundColor: "#fff",
    },
    header: {
        fontWeight: "400",
        borderStyle: "hidden",
        background: "#1d6dc0",
        color: "#ffffff",
        padding: "0 5px 0 5px",
        "&:first-child": {
            borderRight: "none",
        },
        "&:last-child": {
            borderLeft: "none",
        },
        borderTop: "1px solid #165494",
        minHeight: 30,
        lineHeight: "30px",
    },
    action: {
        flex: 1,
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        "& .btn-link": {
            padding: "0",
        },
    },
    actionBody: {
        borderLeft: "1px solid #D5DFE7",
        borderRight: "1px solid #D5DFE7",
    },
    dragHandle: {
        maxWidth: "60px",
        color: "#566F80",
    },
    disabled: {
        color: "#D5DFE7",
    },
    actionWrapper: {
        display: "flex",
        position: "relative",
        justifyContent: "center",
        "& .form-group": {
            marginBottom: "0",
        },
        "& .form-group + .btn, .btn + .btn, .btn + .opacity-overlay + .opacity-overlay--content > .btn": {
            marginLeft: "8px",
        },
        "& .glyphicon-pro": {
            fontSize: 18,
        },
    },
    actionLast: { minWidth: 92 },
    dragHandleWrapper: { whiteSpace: "nowrap" },
};
export const useFormBuilderStyles = createUseStyles(styles);
function reorder(list, startIndex, endIndex) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
}
export function FormBuilder({ columns, staticRows, rows, onRowAdd, onRowUpdate, onRowDelete, onRowReorder, messages, saveState, entityName = "Field", id, }) {
    const classes = useFormBuilderStyles();
    const [isAdding, setIsAdding] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    useEffect(() => {
        if (saveState === "saved") {
            setEditIndex(null);
            setIsAdding(false);
        }
    }, [saveState]);
    const onUpdate = async (values) => {
        await onRowUpdate(values);
    };
    const onAdd = async (values) => {
        await onRowAdd(values);
    };
    const onUpdateCancel = () => setEditIndex(null);
    const onAddCancel = () => setIsAdding(false);
    // `source.index` is mutated to the drop position by dnd-kit's default
    // OptimisticSortingPlugin. If that plugin is ever disabled, `source.index`
    // will stay at `initialIndex` and reorders will silently no-op.
    const onReorder = useCallback((event) => {
        if (event.canceled || !isSortableOperation(event.operation)) {
            return;
        }
        const { source } = event.operation;
        if (!source || source.initialIndex === source.index) {
            return;
        }
        const items = reorder(rows, source.initialIndex, source.index);
        onRowReorder(items);
    }, [rows, onRowReorder]);
    return (React.createElement(React.Fragment, null,
        React.createElement(FormStateStatus, { saveState: saveState, messages: messages }),
        React.createElement("div", { className: classNames(classes.table), role: "grid", id: id },
            React.createElement(FormHeader, { columns: columns }),
            React.createElement(DragDropProvider, { onDragEnd: onReorder },
                React.createElement("div", { className: classNames(classes.body) },
                    staticRows.map((row, index) => (React.createElement(StaticFormRow, { key: `static-${index}`, columns: columns, row: row }))),
                    rows.map((row, index) => (React.createElement(DraggableFormRow, { key: row.id, columns: columns, row: row, index: index, onSubmit: onUpdate, onEdit: () => setEditIndex(index), onCancel: onUpdateCancel, onDelete: () => onRowDelete(row), isEditing: index === editIndex, actionsDisabled: (editIndex !== null && editIndex !== index) || isAdding, loading: saveState === "saving" }))),
                    isAdding && (React.createElement(NewFormRow, { columns: columns, onSubmit: onAdd, onCancel: onAddCancel, loading: saveState === "saving" }))))),
        React.createElement(Button, { label: `Add ${entityName}`, type: "primary", onClick: () => setIsAdding(true), disabled: editIndex !== null || isAdding, ariaLabel: `Add ${entityName}` })));
}
